// import React from 'react';
// import { useParams } from 'react-router-dom';
// import { useTenancy } from '@/contexts/TenancyContext';
// import { AuditorLayout } from '../components/AuditorLayout';

// // Import the original SOA component
// import SOA from '../../soa';

// // Auditor SOA component - read-only version of the SOA page
// const AuditorSOA: React.FC = () => {
//   const { id } = useParams<{ id?: string }>();
//   const { activeTenancy } = useTenancy();

//   // Return the SOA component with isReadOnly=true
//   return (
//     <SOA isReadOnly={true} />
//   );
// };

// export default AuditorSOA;
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTenancy } from '@/contexts/TenancyContext';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious, PaginationEllipsis } from "@/components/ui/pagination";
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';

// Simplified ScrollArea component since the library component may not be available
const ScrollArea: React.FC<{ className?: string; children: React.ReactNode }> = ({ className, children }) => {
  return (
    <div className={`overflow-auto ${className || ''}`}>
      {children}
    </div>
  );
};
import { Search, Download, ExternalLink, FileText, ShieldAlert, CheckCircle, Info, ChevronRight, Eye } from 'lucide-react';
import { AuditorLayout } from '../components/AuditorLayout';

// SOA service import (reusing existing service)
import { getSOAData } from '@/services/soaService';
import {
  Control,
  Framework,
  ImplementationStatus,
  FrameworkStats,
  SOAData,
  emptySOAData
} from '@/types/soa';

// Extended interfaces for linked items
interface LinkedPolicy {
  id: string;
  name: string;
  status: string;
  url?: string;
}

interface LinkedDocument {
  id: string;
  name: string;
  type: string;
  url?: string;
}

interface LinkedRisk {
  id: string;
  name: string;
  riskLevel: string;
  status: string;
}

interface LinkedAssuranceTask {
  id: string;
  name: string;
  dueDate: string;
  status: string;
}

// Extended Control interface with linked items
interface ExtendedControl extends Control {
  linkedPolicies?: LinkedPolicy[];
  linkedDocuments?: LinkedDocument[];
  linkedRisks?: LinkedRisk[];
  linkedAssuranceTasks?: LinkedAssuranceTask[];
}

// Main fetch SOA data function that uses our service layer
const fetchSOAData = async (tenancyId?: string, isModuleEnabled?: (moduleName: string) => boolean): Promise<SOAData> => {
  try {
    const companyID = tenancyId;

    // Use our service to get the SOA data
    const serviceData = await getSOAData(companyID, isModuleEnabled);

    // If no controls were found, use empty data object
    if (!serviceData || serviceData.controls.length === 0) {
      console.log('No controls found for company:', companyID);
      return emptySOAData;
    }

    console.log('Service data:', serviceData);

    // Service data types now directly match component types
    return serviceData;
  } catch (error) {
    console.error('Error fetching SOA data:', error);
    return emptySOAData; // Return empty data on error instead of throwing
  }
};

// Framework Name Component with memoization (simplified version from original SOA)
const FrameworkName: React.FC<{
  frameworkId: string;
  frameworks: Framework[];
}> = React.memo(({ frameworkId, frameworks }) => {
  // Find the framework by ID
  const framework = frameworks.find(f => f.id === frameworkId);
  const name = framework?.name || frameworkId;

  return <span className="break-words">{name}</span>;
});

// Add display name for React.memo component
FrameworkName.displayName = 'FrameworkName';

// Auditor View Modal Component
interface AuditorViewModalProps {
  controlID: string;
  companyID?: string;
  standard?: string;
  onClose?: () => void;
  onNavigateNext?: () => void;
  onNavigatePrevious?: () => void;
}

const AuditorViewModal: React.FC<AuditorViewModalProps> = ({
  controlID,
  companyID,
  standard = 'ISO27001',
  onClose,
  onNavigateNext,
  onNavigatePrevious
}) => {
  const [loading, setLoading] = useState(true);
  const [controlData, setControlData] = useState<any>(null);
  const [policies, setPolicies] = useState<any[]>([]);
  const [risks, setRisks] = useState<any[]>([]);
  const [assuranceTasks, setAssuranceTasks] = useState<any[]>([]);
  const [supportingDocs, setSupportingDocs] = useState<any[]>([]);

  // Screen navigation state
  const [currentPolicyScreen, setCurrentPolicyScreen] = useState(0);
  const [currentAssuranceTask, setCurrentAssuranceTask] = useState(0);
  const [currentRisk, setCurrentRisk] = useState(0);
  const { activeTenancy } = useTenancy();

  useEffect(() => {
    if (!activeTenancy || !activeTenancy.id || activeTenancy.id === '' || activeTenancy.id === "Loading...") return;
    console.log('%c[AuditorView] Loading Tenancy, ID:', activeTenancy.id, 'color: #007acc;')
    const fetchAuditorViewData = async () => {
      setLoading(true);
      try {
        // Import the deiterateAPI utility for authenticated requests
        const { deiterateAPI } = await import('../../../../js/external-js/variableModules/utils/deiterateAPI.util');

        // Make the authenticated API call using the POST method
        const payload = {
          companyID: companyID,
          controlID: controlID,
          standard: standard,
        };

        const result = await deiterateAPI.POST('/soa/getAuditorView', { mode: 'cors', companyID: activeTenancy.id, body: JSON.stringify(payload) });

        // The deiterateAPI utility handles authentication and headers automatically
        if (result.data) {
          setControlData(result.data.controlData);
          setPolicies(result.data.policies || []);
          setRisks(result.data.risks || []);
          setAssuranceTasks(result.data.assuranceTasks || []);
          setSupportingDocs(result.data.supportingDocs || []);
        }
      } catch (error) {
        console.error('Error fetching auditor view data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAuditorViewData();
  }, [controlID, companyID, standard, activeTenancy]);

  // Helper function to navigate policy screens
  const handlePreviousPolicyScreen = () => {
    if (currentPolicyScreen > 0) {
      setCurrentPolicyScreen(currentPolicyScreen - 1);
    }
  };

  const handleNextPolicyScreen = () => {
    if (policies.length > 0 && currentPolicyScreen < getTotalScreenCount() - 1) {
      setCurrentPolicyScreen(currentPolicyScreen + 1);
    }
  };

  // Helper function to get total screen count across all policies
  const getTotalScreenCount = () => {
    return policies.reduce((count, policy) => count + (policy.screens?.length || 0), 0);
  };

  // Helper functions for assurance tasks and risks
  const handlePreviousAssuranceTask = () => {
    if (currentAssuranceTask > 0) {
      setCurrentAssuranceTask(currentAssuranceTask - 1);
    }
  };

  const handleNextAssuranceTask = () => {
    if (assuranceTasks.length > 0 && currentAssuranceTask < assuranceTasks.length - 1) {
      setCurrentAssuranceTask(currentAssuranceTask + 1);
    }
  };

  const handlePreviousRisk = () => {
    if (currentRisk > 0) {
      setCurrentRisk(currentRisk - 1);
    }
  };

  const handleNextRisk = () => {
    if (risks.length > 0 && currentRisk < risks.length - 1) {
      setCurrentRisk(currentRisk + 1);
    }
  };

  // Determine if we're showing ISM or non-ISM content
  const isISM = standard === 'ISM';

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-full max-w-4xl mx-auto bg-secondary">
        <DialogHeader>
          <DialogTitle>Control {controlID}</DialogTitle>
          <DialogDescription>
            Auditor View for {standard}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex flex-col items-center justify-center p-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <p className="mt-4 text-muted-foreground">Loading auditor view...</p>
          </div>
        ) : controlData ? (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold">
                {!isISM && controlData.controlName && `${controlData.controlName}`}
              </h2>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={onNavigatePrevious}>
                  <ChevronRight className="h-4 w-4 rotate-180" /> Previous
                </Button>
                <Button variant="outline" size="sm" onClick={onNavigateNext}>
                  Next <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <Tabs defaultValue="details" className="w-full">
              <TabsList className="grid grid-cols-4 mb-4">
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="policies">Policies</TabsTrigger>
                <TabsTrigger value="assurance">Assurance Tasks</TabsTrigger>
                <TabsTrigger value="risks">Risks</TabsTrigger>
              </TabsList>

              {/* Details Tab */}
              <TabsContent value="details" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Status</div>
                    <Badge
                      variant={isISM
                        ? (controlData.implementationStatus === 'Not applicable' ? 'destructive' : 'success')
                        : (controlData.controlApplicable === true ? 'success' : 'destructive')
                      }
                    >
                      {isISM
                        ? controlData.implementationStatus
                        : (controlData.controlApplicable === true ? 'Yes' : 'No')}
                    </Badge>
                  </div>

                  {isISM && (
                    <>
                      <div className="space-y-2">
                        <div className="text-sm font-medium">Control Owner</div>
                        <div>{controlData.controlOwner || 'N/A'}</div>
                      </div>

                      <div className="space-y-2 col-span-2">
                        <div className="text-sm font-medium">Implementation Details</div>
                        <Textarea
                          readOnly
                          value={controlData.implementationDetails || ''}
                          className="min-h-[100px] resize-none"
                        />
                      </div>

                      <div className="space-y-2 col-span-2">
                        <div className="text-sm font-medium">Implementation Plan</div>
                        <Textarea
                          readOnly
                          value={controlData.implementationPlan || ''}
                          className="min-h-[100px] resize-none"
                        />
                      </div>

                      <div className="space-y-2">
                        <div className="text-sm font-medium">Implementation Start Date</div>
                        <div>{controlData.startDate ? new Date(controlData.startDate).toLocaleDateString() : 'N/A'}</div>
                      </div>

                      <div className="space-y-2">
                        <div className="text-sm font-medium">Implementation End Date</div>
                        <div>{controlData.endDate ? new Date(controlData.endDate).toLocaleDateString() : 'N/A'}</div>
                      </div>
                    </>
                  )}

                  {!isISM && (
                    <div className="space-y-2 col-span-2">
                      <div className="text-sm font-medium">Control Justification</div>
                      <Textarea
                        readOnly
                        value={controlData.controlJustification || ''}
                        className="min-h-[100px] resize-none"
                      />
                    </div>
                  )}

                  {/* Supporting Documents */}
                  {supportingDocs.length > 0 && (
                    <div className="col-span-2 space-y-2">
                      <div className="text-sm font-medium">Supporting Documents</div>
                      <div className="flex flex-wrap gap-2">
                        {supportingDocs.map((doc, index) => (
                          <a
                            key={index}
                            href={doc.downloadURL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-primary hover:underline"
                          >
                            <FileText className="h-4 w-4" />
                            {doc.fileName}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Policies Tab */}
              <TabsContent value="policies" className="space-y-4">
                {policies.length > 0 ? (
                  <>
                    <div className="bg-muted/50 rounded-lg p-4 min-h-[300px]">
                      {policies.map((policy, pIndex) => (
                        <React.Fragment key={policy.policyID}>
                          {policy.screens.map((policyScreen, sIndex) => {
                            const screenIndex = policies.slice(0, pIndex).reduce(
                              (acc, p) => acc + (p.screens?.length || 0), 0
                            ) + sIndex;

                            if (screenIndex !== currentPolicyScreen) return null;

                            const screenLayout = policyScreen.screenData.screenLayout;

                            return (
                              <div key={`${policy.policyID}_${policyScreen.screenID}`}>
                                <h3 className="text-lg font-semibold mb-3">
                                  {policy.policyName} - {policyScreen.screenData.procedure_1.proc_name}
                                </h3>

                                {screenLayout === '1' && (
                                  <ol className="list-[lower-alpha] pl-5 space-y-2">
                                    {Object.entries(policyScreen.screenData.procedure_1)
                                      .filter(([key]) => key.startsWith('proc_step'))
                                      .map(([key, step]: [string, any]) => (
                                        <li
                                          key={key}
                                          className={step.highlight ? 'text-orange-500' : ''}
                                          dangerouslySetInnerHTML={{
                                            __html: step.clause_text.toString()
                                              .replace(/<([^>]+)>/g, '<span class="text-orange-500">$1</span>')
                                          }}
                                        />
                                      ))
                                    }
                                  </ol>
                                )}

                                {screenLayout === '2' && (
                                  <ul className="list-none pl-0 space-y-3">
                                    {Object.entries(policyScreen.screenData.procedure_1)
                                      .filter(([key]) => key.startsWith('proc_step'))
                                      .map(([key, step]: [string, any]) => {
                                        if (typeof step.clause_text === 'string') {
                                          return (
                                            <li
                                              key={key}
                                              className={step.highlight ? 'text-orange-500' : ''}
                                              dangerouslySetInnerHTML={{
                                                __html: step.clause_text.toString()
                                                  .replace(/<([^>]+)>/g, '<span class="text-orange-500">$1</span>')
                                              }}
                                            />
                                          );
                                        } else {
                                          // Complex structure with nested items
                                          return (
                                            <li key={key} className="space-y-2">
                                              {step.clause_text.text && (
                                                <div
                                                  dangerouslySetInnerHTML={{
                                                    __html: step.clause_text.text.toString()
                                                      .replace(/<([^>]+)>/g, '<span class="text-orange-500">$1</span>')
                                                  }}
                                                />
                                              )}
                                              <ul className="list-disc pl-5">
                                                {Object.entries(step.clause_text)
                                                  .filter(([subKey]) => subKey !== 'text')
                                                  .map(([subKey, subText]: [string, any]) => (
                                                    <li
                                                      key={subKey}
                                                      dangerouslySetInnerHTML={{
                                                        __html: subText.toString()
                                                          .replace(/<([^>]+)>/g, '<span class="text-orange-500">$1</span>')
                                                      }}
                                                    />
                                                  ))
                                                }
                                              </ul>
                                            </li>
                                          );
                                        }
                                      })
                                    }
                                  </ul>
                                )}

                                {screenLayout === '3' && (
                                  <ol className="list-[lower-alpha] pl-5 space-y-2">
                                    {Object.entries(policyScreen.screenData)
                                      .filter(([key]) => key.startsWith('procedure'))
                                      .map(([key, proc]: [string, any]) => (
                                        <li
                                          key={key}
                                          className={proc.proc_step_a.highlight ? 'text-orange-500' : ''}
                                          dangerouslySetInnerHTML={{
                                            __html: `${proc.proc_name} - ${proc.proc_step_a.clause_text.toString()}`
                                              .replace(/<([^>]+)>/g, '<span class="text-orange-500">$1</span>')
                                          }}
                                        />
                                      ))
                                    }
                                  </ol>
                                )}
                              </div>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </div>

                    {/* Navigation controls */}
                    <div className="flex justify-between items-center">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handlePreviousPolicyScreen}
                        disabled={currentPolicyScreen === 0}
                      >
                        <ChevronRight className="h-4 w-4 rotate-180" /> Previous
                      </Button>

                      <div className="text-sm text-muted-foreground">
                        Page {currentPolicyScreen + 1} of {getTotalScreenCount()}
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleNextPolicyScreen}
                        disabled={currentPolicyScreen >= getTotalScreenCount() - 1}
                      >
                        Next <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No policy information available for this control.
                  </div>
                )}
              </TabsContent>

              {/* Assurance Tasks Tab */}
              <TabsContent value="assurance" className="space-y-4">
                {assuranceTasks.length > 0 ? (
                  <>
                    <div className="bg-muted/50 rounded-lg p-4 min-h-[200px]">
                      {assuranceTasks.map((task, index) => {
                        if (index !== currentAssuranceTask) return null;

                        // Extract task details
                        const checkHistory = task.assuranceData.checkHistory || {};
                        const numberOfChecks = checkHistory.numberPreviousChecks || 0;
                        const latestCheck = numberOfChecks > 0 ? checkHistory[numberOfChecks] : null;

                        // Calculate scheduled date and status
                        let scheduledDate = 'N/A';
                        let status = 'Incomplete';

                        if (latestCheck) {
                          const dateObj = new Date(0); // Epoch time
                          dateObj.setSeconds(latestCheck.scheduledDate?._seconds || 0);
                          scheduledDate = dateObj.toLocaleDateString();

                          if (dateObj.getTime() <= new Date().getTime() && parseInt(numberOfChecks) > 1) {
                            status = 'Completed';
                          }
                        }

                        return (
                          <div key={index} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <div className="text-sm font-medium">Scheduled Date</div>
                                <div className="text-orange-500">{scheduledDate}</div>
                              </div>

                              <div>
                                <div className="text-sm font-medium">Status</div>
                                <div className="text-orange-500">{status}</div>
                              </div>

                              <div className="col-span-2">
                                <div className="text-sm font-medium">Evidence</div>
                                {latestCheck?.evidence?.length > 0 ? (
                                  <div className="flex flex-wrap gap-2">
                                    {latestCheck.evidence.map((doc: string, i: number) => (
                                      <Button
                                        key={i}
                                        variant="link"
                                        className="text-orange-500 p-0 h-auto"
                                        onClick={() => {
                                          // Implement download functionality here
                                          console.log('Download evidence:', doc);
                                          alert('Download functionality would be implemented here');
                                        }}
                                      >
                                        <FileText className="h-4 w-4 mr-1" /> View
                                      </Button>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-sm text-muted-foreground">No evidence available</div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Navigation controls */}
                    <div className="flex justify-between items-center">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handlePreviousAssuranceTask}
                        disabled={currentAssuranceTask === 0}
                      >
                        <ChevronRight className="h-4 w-4 rotate-180" /> Previous
                      </Button>

                      <div className="text-sm text-muted-foreground">
                        Task {currentAssuranceTask + 1} of {assuranceTasks.length}
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleNextAssuranceTask}
                        disabled={currentAssuranceTask >= assuranceTasks.length - 1}
                      >
                        Next <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No assurance tasks available for this control.
                  </div>
                )}
              </TabsContent>

              {/* Risks Tab */}
              <TabsContent value="risks" className="space-y-4">
                {risks.length > 0 ? (
                  <>
                    <div className="bg-muted/50 rounded-lg p-4 min-h-[200px]">
                      {risks.map((risk, index) => {
                        if (index !== currentRisk) return null;

                        return (
                          <div key={index} className="space-y-4">
                            <div>
                              <div className="text-sm font-medium">Risk ID</div>
                              <div className="text-gray-700">{risk.riskID}</div>
                            </div>

                            <div>
                              <div className="text-sm font-medium">Risk Description</div>
                              <div className="text-gray-700">
                                {(risk.riskData.risk || '').replace(/ACME INC/gi, 'Your Company')}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Navigation controls */}
                    <div className="flex justify-between items-center">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handlePreviousRisk}
                        disabled={currentRisk === 0}
                      >
                        <ChevronRight className="h-4 w-4 rotate-180" /> Previous
                      </Button>

                      <div className="text-sm text-muted-foreground">
                        Risk {currentRisk + 1} of {risks.length}
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleNextRisk}
                        disabled={currentRisk >= risks.length - 1}
                      >
                        Next <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No risks available for this control.
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            No data available for this control.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Helper function to get auth token - this needs to be implemented based on your auth system
const getValidToken = async () => {
  // This is just a placeholder - replace with your actual auth token retrieval logic
  return 'your-auth-token';
};

// Control Detail Sheet Component
const ControlDetailSheet: React.FC<{
  control: ExtendedControl;
  frameworks: Framework[];
}> = ({ control, frameworks }) => {
  // Find framework name
  const framework = frameworks.find(f => f.id === control.frameworkId);
  const frameworkName = framework?.name || control.frameworkId;

  return (
    <div className="p-1">
      <SheetHeader className="pb-4">
        <SheetTitle className="text-xl flex items-center gap-2">
          <Badge variant="outline" className="px-2 py-1 font-mono text-xs">
            {control.controlID}
          </Badge>
          {control.name}
        </SheetTitle>
        <SheetDescription className="text-sm mt-2">
          {control.description}
        </SheetDescription>
      </SheetHeader>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-sm font-medium">Framework</p>
          <p className="text-sm text-muted-foreground">{frameworkName}</p>
        </div>
        <div>
          <p className="text-sm font-medium">Status</p>
          <Badge
            variant={control.status === 'Fully implemented' ? 'success' :
              control.status === 'Partially implemented' ? 'warning' :
                control.status === 'Not implemented' ? 'destructive' : 'secondary'}
            className="mt-1">
            {control.status}
          </Badge>
        </div>
      </div>

      <div className="mb-4">
        <p className="text-sm font-medium mb-1">Applicability</p>
        <Badge
          variant={control.applicable ? 'outline' : 'secondary'}
          className="mb-2">
          {control.applicable ? 'Included' : 'Excluded'}
        </Badge>
        <div className="text-sm p-3 bg-muted/50 rounded-md">
          <p className="font-medium mb-1">Justification:</p>
          <p className="text-muted-foreground">{control.justification || 'No justification provided'}</p>
        </div>
      </div>

      <Tabs defaultValue="policies" className="mt-6">
        <TabsList className="grid grid-cols-4">
          <TabsTrigger value="policies">Policies</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="risks">Risks</TabsTrigger>
          <TabsTrigger value="tasks">Tasks</TabsTrigger>
        </TabsList>

        <TabsContent value="policies" className="p-1">
          <div className="text-sm text-muted-foreground mb-2">Policies linked to this control:</div>
          <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
            {linkedPolicies.length > 0 ? linkedPolicies.map(policy => (
              <div key={policy.id} className="border rounded-md p-3 flex items-center justify-between hover:bg-muted/50 transition-colors">
                <div>
                  <p className="font-medium">{policy.name}</p>
                  <p className="text-xs text-muted-foreground">{policy.id} · {policy.status}</p>
                </div>
                <Button variant="ghost" size="sm" asChild>
                  <Link to={policy.url || '#'} className="flex items-center gap-1">
                    <span>Open</span>
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                </Button>
              </div>
            )) : (
              <div className="text-center py-8 text-muted-foreground">
                <p>No policies linked to this control</p>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="documents" className="p-1">
          <div className="text-sm text-muted-foreground mb-2">Supporting documents for this control:</div>
          <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
            {linkedDocuments.length > 0 ? linkedDocuments.map(doc => (
              <div key={doc.id} className="border rounded-md p-3 flex items-center justify-between hover:bg-muted/50 transition-colors">
                <div className="flex items-start gap-3">
                  <FileText className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-medium">{doc.name}</p>
                    <p className="text-xs text-muted-foreground">{doc.id} · {doc.type}</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" asChild>
                  <Link to={doc.url || '#'} className="flex items-center gap-1">
                    <span>Open</span>
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                </Button>
              </div>
            )) : (
              <div className="text-center py-8 text-muted-foreground">
                <p>No documents linked to this control</p>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="risks" className="p-1">
          <div className="text-sm text-muted-foreground mb-2">Risks associated with this control:</div>
          <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
            {linkedRisks.length > 0 ? linkedRisks.map(risk => (
              <div key={risk.id} className="border rounded-md p-3 flex items-center justify-between hover:bg-muted/50 transition-colors">
                <div className="flex items-start gap-3">
                  <ShieldAlert className="h-5 w-5 text-destructive flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-medium">{risk.name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-xs">{risk.id}</Badge>
                      <Badge variant="outline" className="text-xs">{risk.riskLevel}</Badge>
                      <Badge variant={risk.status === 'Mitigated' ? 'success' : 'secondary'} className="text-xs">{risk.status}</Badge>
                    </div>
                  </div>
                </div>
                <Button variant="ghost" size="sm" asChild>
                  <Link to={`/risk-detail/${risk.id}`} className="flex items-center gap-1">
                    <span>Open</span>
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                </Button>
              </div>
            )) : (
              <div className="text-center py-8 text-muted-foreground">
                <p>No risks linked to this control</p>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="tasks" className="p-1">
          <div className="text-sm text-muted-foreground mb-2">Assurance tasks for this control:</div>
          <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
            {linkedTasks.length > 0 ? linkedTasks.map(task => (
              <div key={task.id} className="border rounded-md p-3 flex items-center justify-between hover:bg-muted/50 transition-colors">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-medium">{task.name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-xs">{task.id}</Badge>
                      <p className="text-xs text-muted-foreground">Due: {task.dueDate}</p>
                      <Badge variant={task.status === 'Completed' ? 'success' : 'secondary'} className="text-xs">{task.status}</Badge>
                    </div>
                  </div>
                </div>
                <Button variant="ghost" size="sm" asChild>
                  <Link to={`/tasks/${task.id}`} className="flex items-center gap-1">
                    <span>Open</span>
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                </Button>
              </div>
            )) : (
              <div className="text-center py-8 text-muted-foreground">
                <p>No assurance tasks linked to this control</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Function to toggle the AuditorViewModal in React
const toggleAuditorViewModal = (controlID: string, companyID?: string, standard: string = 'ISO27001') => {
  // This would be implemented within the component to access state
  // The actual implementation is in the AuditorSOA component
};

// Auditor SOA component - read-only version of the SOA page
const AuditorSOA: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const { activeTenancy, isModuleEnabled } = useTenancy();
  const [tenancyId, setTenancyId] = useState<string | null>(null);

  // Modal state
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [currentControlId, setCurrentControlId] = useState<string>('');
  const [selectedStandard, setSelectedStandard] = useState<string>('ISO27001');

  // Function to open the auditor view modal
  const openAuditorViewModal = (controlID: string, standard: string = 'ISO27001') => {
    console.log('Opening modal for control:', controlID, 'with standard:', standard);
    setCurrentControlId(controlID);
    setSelectedStandard(standard);
    setModalOpen(true);
    console.log('Modal state after open:', true);
  };

  // Functions to navigate between controls
  const goToNextControl = () => {
    const currentIndex = paginatedControls.findIndex(c => c.controlID === currentControlId);
    if (currentIndex < paginatedControls.length - 1) {
      setCurrentControlId(paginatedControls[currentIndex + 1].controlID);
    }
  };

  const goToPreviousControl = () => {
    const currentIndex = paginatedControls.findIndex(c => c.controlID === currentControlId);
    if (currentIndex > 0) {
      setCurrentControlId(paginatedControls[currentIndex - 1].controlID);
    }
  };

  // State for filters
  const [framework, setFramework] = useState<string | null>(null);
  const [implementationStatus, setImplementationStatus] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showJustification, setShowJustification] = useState<boolean>(false);
  const [selectedControl, setSelectedControl] = useState<ExtendedControl | null>(null);
  const [soaData, setSoaData] = useState<SOAData | null>(null);
  // const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Set tenancy ID when activeTenancy changes
  useEffect(() => {
    console.log('[AuditorSOA] Active tenancy:', activeTenancy);
    if (!activeTenancy || activeTenancy.name === "Loading...") return;
    console.log('[AuditorSOA] isModuleEnabled:', isModuleEnabled);
    console.log('[AuditorSOA] isModuleEnabled ISO 27001:', isModuleEnabled('ISO 27001'));
    console.log('[AuditorSOA] isModuleEnabled ISO27001:', isModuleEnabled('ISO27001'));
    setTenancyId(activeTenancy.id);
  }, [activeTenancy, isModuleEnabled]);

  // Create a stable reference to the isModuleEnabled function
  const isModuleEnabledRef = useRef(isModuleEnabled);
  isModuleEnabledRef.current = isModuleEnabled;

  // Get module status for query key
  const isISO27001Enabled = isModuleEnabled('ISO 27001') || isModuleEnabled('ISO27001');

  // Fetch SOA data when tenancyId or module status changes
  const {
    data: queryData,
    error,
    isLoading: isQueryLoading,
    refetch: refetchSOAData
  } = useQuery<SOAData, Error>({
    queryKey: ['soaData', tenancyId, isISO27001Enabled],
    queryFn: () => {
      if (!tenancyId || tenancyId === 'Loading...') {
        console.error('No tenancy ID available');
        return Promise.resolve(emptySOAData);
      }
      console.log('[AuditorSOA] Fetching SOA data with tenancyId:', tenancyId);
      return fetchSOAData(tenancyId, isModuleEnabledRef.current);
    },
    enabled: !!tenancyId, // Only run the query if we have a tenancyId
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false
  });

  // Update soaData when query data changes
  useEffect(() => {
    if (queryData) {
      console.log('SOA data:', queryData);
      setSoaData(queryData);
      setIsLoading(false);
    }
  }, [queryData]);

  // Filter functions
  const matchesFramework = useCallback(
    (control: Control): boolean => {
      if (!framework) return true;
      return control.frameworkId === framework;
    },
    [framework]
  );

  const matchesSearch = useCallback(
    (control: Control): boolean => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      return (
        (typeof control.id === 'string' && control.id.toLowerCase().includes(searchLower)) ||
        (typeof control.controlID === 'string' && control.controlID.toLowerCase().includes(searchLower)) ||
        (typeof control.name === 'string' && control.name.toLowerCase().includes(searchLower)) ||
        (typeof control.description === 'string' && control.description.toLowerCase().includes(searchLower)) ||
        (typeof control.justification === 'string' && control.justification.toLowerCase().includes(searchLower))
      );
    },
    [searchTerm]
  );

  const matchesStatus = useCallback(
    (control: Control): boolean => {
      if (!implementationStatus || implementationStatus === 'all') return true;
      return control.status === implementationStatus;
    },
    [implementationStatus]
  );

  // Apply all filters
  const filteredControls = useMemo(() => {
    if (!soaData || !soaData.controls) return [];
    // Type assertion to fix TypeScript errors
    return (soaData.controls as Control[]).filter(
      (control) => matchesFramework(control) && matchesSearch(control) && matchesStatus(control)
    );
  }, [soaData, matchesFramework, matchesSearch, matchesStatus]);

  // Get paginated controls
  const paginatedControls = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredControls.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredControls, currentPage, itemsPerPage]);

  // Calculate total pages
  const totalPages = useMemo(() => {
    return Math.ceil(filteredControls.length / itemsPerPage);
  }, [filteredControls.length, itemsPerPage]);

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Define a custom FrameworkStatsExtended interface that includes the fields we need
  interface FrameworkStatsExtended {
    id: string;       // Original from FrameworkStats
    name: string;     // Original from FrameworkStats
    frameworkId: string;  // Added for our implementation
    frameworkName: string; // Added for our implementation
    total: number;
    applicable: number;
    implemented: number;
    partiallyImplemented: number;
    notImplemented: number;
    notApplicable: number;
    implementationRate: number;
  }

  // Calculate framework statistics and overall applicability summary
  const { frameworkStats, applicabilitySummary } = useMemo(() => {
    if (!soaData || !soaData.controls) return { frameworkStats: [], applicabilitySummary: { total: 0, included: 0, excluded: 0, includedPercent: 0, excludedPercent: 0 } };

    const stats: FrameworkStatsExtended[] = [];
    const frameworks = soaData.frameworks || [];

    // Calculate overall applicability summary first
    const controls = soaData.controls as Control[];
    const total = controls.length;
    const included = controls.filter(c => c.applicable).length;
    const excluded = controls.filter(c => !c.applicable).length;

    const applicabilitySummary = {
      total,
      included,
      excluded,
      includedPercent: total > 0 ? Math.round((included / total) * 100) : 0,
      excludedPercent: total > 0 ? Math.round((excluded / total) * 100) : 0
    };

    // Group controls by framework with type assertion to fix TypeScript errors
    const controlsByFramework = controls.reduce((acc, control) => {
      const frameworkId = control.frameworkId;
      if (!acc[frameworkId]) {
        acc[frameworkId] = [];
      }
      acc[frameworkId].push(control);
      return acc;
    }, {} as Record<string, Control[]>);

    // Calculate stats for each framework
    Object.entries(controlsByFramework).forEach(([frameworkId, controls]) => {
      const framework = frameworks.find(f => f.id === frameworkId);
      const frameworkName = framework?.name || frameworkId;

      const total = controls.length;
      const applicable = controls.filter(c => c.applicable).length;
      const implemented = controls.filter(c => c.applicable && c.status === 'Fully implemented').length;
      const partiallyImplemented = controls.filter(c => c.applicable && c.status === 'Partially implemented').length;
      const notImplemented = controls.filter(c => c.applicable && c.status === 'Not implemented').length;
      const notApplicable = controls.filter(c => !c.applicable).length;

      stats.push({
        id: frameworkId,    // Map to original FrameworkStats fields
        name: frameworkName,  // Map to original FrameworkStats fields
        frameworkId,
        frameworkName,
        total,
        applicable,
        implemented,
        partiallyImplemented,
        notImplemented,
        notApplicable,
        implementationRate: applicable > 0 ? Math.round((implemented / applicable) * 100) : 0
      });
    });

    return { frameworkStats: stats, applicabilitySummary };
  }, [soaData]);

  // Get status badge color
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'Fully implemented':
        return 'success';
      case 'Partially implemented':
        return 'warning';
      case 'Not implemented':
        return 'destructive';
      case 'Not applicable':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  // Return the component JSX
  return (
    <>
      <div className="page-container">
        <div className="glass-card max-w-full overflow-x-auto">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-semibold">Statement of Applicability (SOA)</h1>

            <div className="flex items-center gap-2">
              <a href="#" className="flex items-center gap-1 text-primary hover:underline" onClick={(e) => {
                e.preventDefault();
                // Here you would implement export functionality
                alert('Export functionality would be implemented here');
              }}>
                <Download className="w-4 h-4" />
                <span>Export</span>
              </a>
            </div>
          </div>

          {/* Framework Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {isLoading ? (
              <>
                <Skeleton className="h-28 w-full" />
                <Skeleton className="h-28 w-full" />
                <Skeleton className="h-28 w-full" />
              </>
            ) : (
              frameworkStats.map((stat) => (
                <Card key={stat.frameworkId} className="glass-card overflow-hidden">
                  <CardHeader className="p-4 pb-2">
                    <CardTitle className="text-lg font-medium">{stat.frameworkName}</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 pt-0">
                    <div className="mt-2">
                      <div className="flex justify-between mb-1">
                        <span className="text-sm text-muted-foreground">Implementation Rate</span>
                        <span className="text-sm font-medium">{stat.implementationRate}%</span>
                      </div>
                      <div className="w-full bg-secondary h-2 rounded-full overflow-hidden">
                        <div
                          className="bg-primary h-full"
                          style={{ width: `${stat.implementationRate}%` }}
                        ></div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-3">
                      <div className="text-xs">
                        <span className="text-muted-foreground">Implemented:</span>{' '}
                        <span className="font-medium">{stat.implemented}</span>
                      </div>
                      <div className="text-xs">
                        <span className="text-muted-foreground">Partial:</span>{' '}
                        <span className="font-medium">{stat.partiallyImplemented}</span>
                      </div>
                      <div className="text-xs">
                        <span className="text-muted-foreground">Not Implemented:</span>{' '}
                        <span className="font-medium">{stat.notImplemented}</span>
                      </div>
                      <div className="text-xs">
                        <span className="text-muted-foreground">Not Applicable:</span>{' '}
                        <span className="font-medium">{stat.notApplicable}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search controls..."
                  className="pl-8"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div className="w-full md:w-48">
              <Select value={framework || 'all'} onValueChange={(value) => setFramework(value === 'all' ? null : value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Framework" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Frameworks</SelectItem>
                  {soaData?.frameworks?.map((fw) => (
                    <SelectItem key={fw.id} value={fw.id}>
                      {fw.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="w-full md:w-48">
              <Select value={implementationStatus || 'all'} onValueChange={(value) => setImplementationStatus(value === 'all' ? null : value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="Fully implemented">Fully implemented</SelectItem>
                  <SelectItem value="Partially implemented">Partially implemented</SelectItem>
                  <SelectItem value="Not implemented">Not implemented</SelectItem>
                  <SelectItem value="Not applicable">Not applicable</SelectItem>
                  <SelectItem value="Unknown">Unknown</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Controls Table */}
        <div className="table-wrapper rounded-lg overflow-x-auto max-w-full">
          <Table className="table w-full min-w-[800px]">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[100px]">Control ID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead className="w-[150px]">Framework</TableHead>
                <TableHead className="w-[120px] text-center">Status</TableHead>
                <TableHead className="w-[100px] text-center">Actions</TableHead>
                <TableHead className="w-[100px] text-center">Auditor View</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-full" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-28" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-28 mx-auto" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-20 mx-auto" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-20 mx-auto" /></TableCell>
                  </TableRow>
                ))
              ) : paginatedControls.length > 0 ? (
                paginatedControls.map((control) => (
                  <TableRow key={control.id} className="hover:bg-muted/50">
                    <TableCell className="font-medium">{control.controlID}</TableCell>
                    <TableCell className="max-w-xs min-w-[200px]">
                      <div className="font-medium break-words whitespace-normal">{control.name}</div>
                      <div className="text-sm text-muted-foreground line-clamp-2 break-words">{control.description}</div>
                    </TableCell>
                    <TableCell>
                      {soaData?.frameworks && (
                        <FrameworkName frameworkId={control.frameworkId} frameworks={soaData.frameworks} />
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant={getStatusBadgeVariant(control.status) as any} className="mx-auto">
                        {control.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex flex-wrap gap-1 justify-center">
                        <Badge variant="outline" className="text-xs">
                          {control.applicable ? 'Applicable' : 'Not Applicable'}
                        </Badge>
                        {control.justification && (
                          <Badge variant="outline" className="text-xs">
                            Justified
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Button size="sm" variant="default" onClick={() => openAuditorViewModal(control.controlID, control.frameworkId)}>
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-6 text-muted-foreground">
                    No controls found matching your filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-6">
            <Pagination>
              <PaginationContent className="flex flex-wrap">
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    className={currentPage === 1 ? 'pointer-events-none opacity-50' : ''}
                    size="sm"
                  />
                </PaginationItem>

                {/* First page */}
                <PaginationItem>
                  <PaginationLink
                    onClick={() => handlePageChange(1)}
                    isActive={currentPage === 1}
                    size="sm"
                  >
                    1
                  </PaginationLink>
                </PaginationItem>

                {/* Ellipsis if needed */}
                {currentPage > 3 && (
                  <PaginationItem>
                    <PaginationEllipsis />
                  </PaginationItem>
                )}

                {/* Pages around current page */}
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => {
                    // Only show pages around current page
                    return page !== 1 &&
                      page !== totalPages &&
                      (Math.abs(page - currentPage) <= 1 ||
                        (page === 2 && currentPage === 1) ||
                        (page === totalPages - 1 && currentPage === totalPages));
                  })
                  .map((page) => (
                    <PaginationItem key={page}>
                      <PaginationLink
                        onClick={() => handlePageChange(page)}
                        isActive={currentPage === page}
                        size="sm"
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  ))
                }

                {/* Ellipsis if needed */}
                {currentPage < totalPages - 2 && (
                  <PaginationItem>
                    <PaginationEllipsis />
                  </PaginationItem>
                )}

                {/* Last page */}
                {totalPages > 1 && (
                  <PaginationItem>
                    <PaginationLink
                      onClick={() => handlePageChange(totalPages)}
                      isActive={currentPage === totalPages}
                      size="sm"
                    >
                      {totalPages}
                    </PaginationLink>
                  </PaginationItem>
                )}

                <PaginationItem>
                  <PaginationNext
                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                    className={currentPage === totalPages ? 'pointer-events-none opacity-50' : ''}
                    size="sm"
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </div>

      {modalOpen && (
        <AuditorViewModal
          controlID={currentControlId}
          companyID={activeTenancy?.id}
          standard={selectedStandard}
          onClose={() => setModalOpen(false)}
          onNavigateNext={goToNextControl}
          onNavigatePrevious={goToPreviousControl}
        />
      )}
    </>
  );
};

export default AuditorSOA;
